"use client";

import React from "react";
import * as AriaKit from "@ariakit/react";
import Button from "./Button";
import Icon from "../../common/components/Icon";
import { useTranslations } from "next-intl";

export interface ModalProps {
  open: boolean;
  icon?: string;
  hasCloseBtn?: boolean;
  actions?: React.ReactNode /* @deprecated Add the Button to the bottom of children, use ModalActions component for stickyness */;
  title: string | React.ReactNode;
  stickyActions?: boolean;
  wide?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  style?: any;
  ref?: any;
  hideOnInteractOutside?: boolean;
}

const ModalActions: React.FC<{
  sticky: boolean;
  children: React.ReactNode;
}> = ({ children, sticky }) => {
  return (
    <div
      className={`bottom-0 start-0 p-3 end-0 color-bg-white py-2 w-100 shadow-top ${sticky ? "position-sticky" : ""}`}
    >
      {children}
    </div>
  );
};

const Modal: React.FC<ModalProps> = React.forwardRef<any, ModalProps>(
  (
    {
      open,
      icon,
      onClose,
      actions,
      wide,
      children,
      title,
      style,
      hideOnInteractOutside,
    },
    ref,
  ) => {
    const dialog = AriaKit.useDialogStore();
    const t = useTranslations();

    return (
      <AriaKit.Dialog
        open={open}
        onClose={onClose}
        store={dialog}
        backdrop={<div className="backdrop" />}
        className={`dialog ${wide ? "dialog-wide" : ""}`}
        portal
        getPersistentElements={() => document.querySelectorAll(".toaster")}
        style={style}
        hideOnInteractOutside={hideOnInteractOutside}
        autoFocusOnShow={false}
      >
        <div
          className="scrollbar-thin overflow-y-auto overflow-x-hidden"
          ref={ref}
        >
          <div className="position-sticky p-3 px-lg-4 top-0 color-bg-white shadow-and-blur-on-scroll z-3 border-bottom">
            <div className="d-flex justify-content-between gap-2 align-items-center">
              <h6 className="d-flex align-items-center gap-2 my-0 fw-semibold dialog-title">
                {icon && <Icon icon={icon} className="hide-on-mobile" />}
                {title}
              </h6>
              <Button
                appearance="link"
                icon="close"
                iconPosition="right"
                className="fs-8 color-primary-dimmed"
                onClick={onClose}
              >
                <span className="hide-on-mobile">{t("close")}</span>
              </Button>
            </div>
          </div>
          <div className="p-3 px-lg-4">{children}</div>
          {actions && <ModalActions sticky={true}>{actions}</ModalActions>}
        </div>
      </AriaKit.Dialog>
    );
  },
);

Modal.displayName = "Modal";

export { ModalActions };

export default Modal;
